import { QRCodeSVG } from 'qrcode.react';
import type { KioskConfigDTO, KioskSessionView } from '@print-saas/shared';
import type { ReactNode } from 'react';
import { PayIcon, ScanIcon, UploadIcon } from '../components/icons';
import { formatPrice } from '../lib/format';

interface WaitingScreenProps {
  session: KioskSessionView;
  config: KioskConfigDTO | null;
}

const steps: { icon: ReactNode; title: string; text: string }[] = [
  { icon: <ScanIcon />, title: 'Scan', text: 'Point your phone camera at the code' },
  { icon: <UploadIcon />, title: 'Upload & choose', text: 'Pick files, colour, copies, sides' },
  { icon: <PayIcon />, title: 'Pay & collect', text: 'Pay by UPI / card, prints come out here' },
];

export function WaitingScreen({ session, config }: WaitingScreenProps) {
  return (
    <div className="flex h-full w-full items-center justify-center gap-[6vmin] px-[5vmin] portrait:flex-col portrait:gap-[4vmin] landscape:flex-row">
      {/* Headline (portrait: above the QR) */}
      <div className="flex max-w-[95vmin] flex-col gap-[3.5vmin] portrait:items-center portrait:text-center landscape:w-[min(46vw,90vmin)]">
        <div>
          <p className="text-[2.6vmin] font-semibold tracking-[0.2em] text-accent uppercase">
            Print from your phone
          </p>
          <h1 className="mt-2 text-[10vmin] leading-[0.95] font-black tracking-tight">
            Scan to print
          </h1>
        </div>

        <ol className="flex flex-col gap-[2vmin] portrait:hidden">
          {steps.map((s, i) => (
            <Step key={s.title} n={i + 1} {...s} />
          ))}
        </ol>

        <div className="portrait:hidden">
          <PriceCard config={config} />
        </div>
      </div>

      <QrBlock url={session.url} />

      {/* Portrait: steps and price below the QR */}
      <div className="flex w-full max-w-[92vmin] flex-col gap-[3vmin] landscape:hidden">
        <ol className="grid grid-cols-3 gap-[2.4vmin]">
          {steps.map((s, i) => (
            <CompactStep key={s.title} n={i + 1} {...s} />
          ))}
        </ol>
        <PriceCard config={config} />
      </div>
    </div>
  );
}

function QrBlock({ url }: { url: string }) {
  return (
    <div className="flex shrink-0 flex-col items-center gap-[2.4vmin]">
      <div className="qr-frame rounded-[3.5vmin] bg-white p-[2.2vmin] landscape:size-[64vmin] portrait:size-[58vmin]">
        {/* Remount on URL change so the new code fades in with the screen. */}
        <QRCodeSVG
          key={url}
          value={url}
          level="H"
          marginSize={2}
          bgColor="#ffffff"
          fgColor="#0a1020"
          size={512}
          title="Scan to start printing"
          style={{ width: '100%', height: '100%', display: 'block' }}
        />
      </div>
      <p className="flex items-center gap-2 text-[2.4vmin] text-muted">
        <span className="inline-block size-[1.4vmin] animate-pulse-dot rounded-full bg-go" />
        Open your camera app and point it here
      </p>
    </div>
  );
}

function Step({ n, icon, title, text }: { n: number; icon: ReactNode; title: string; text: string }) {
  return (
    <li className="flex items-center gap-[2.4vmin] rounded-[2.4vmin] bg-panel/80 px-[2.6vmin] py-[2vmin] ring-1 ring-line">
      <span className="grid size-[7.5vmin] shrink-0 place-items-center rounded-[1.8vmin] bg-accent/15 text-accent">
        <span className="size-[4.6vmin]">{icon}</span>
      </span>
      <div className="min-w-0">
        <div className="text-[3.3vmin] leading-tight font-bold">
          <span className="text-accent">{n}.</span> {title}
        </div>
        <div className="text-[2.2vmin] leading-snug text-muted">{text}</div>
      </div>
    </li>
  );
}

function CompactStep({ n, icon, title }: { n: number; icon: ReactNode; title: string }) {
  return (
    <li className="flex flex-col items-center gap-[1.2vmin] rounded-[2.4vmin] bg-panel/80 px-[1.5vmin] py-[2.2vmin] text-center ring-1 ring-line">
      <span className="grid size-[8vmin] place-items-center rounded-[1.8vmin] bg-accent/15 text-accent">
        <span className="size-[5vmin]">{icon}</span>
      </span>
      <div className="text-[2.7vmin] leading-tight font-bold">
        <span className="text-accent">{n}.</span> {title}
      </div>
    </li>
  );
}

function PriceCard({ config }: { config: KioskConfigDTO | null }) {
  if (!config) return null;
  const { pricing } = config;
  const lowPaper = config.paperCount <= config.lowPaperThreshold;
  return (
    <div className="rounded-[2.4vmin] bg-ink-2/80 px-[3vmin] py-[2.4vmin] ring-1 ring-line">
      <div className="flex flex-wrap items-end gap-x-[5vmin] gap-y-[1.5vmin] portrait:justify-center">
        <Price label="Black & white" value={formatPrice(pricing.bwPricePerPage)} />
        {pricing.colorEnabled && (
          <Price label="Colour" value={formatPrice(pricing.colorPricePerPage)} colorful />
        )}
      </div>
      <div className="mt-[1.4vmin] flex flex-wrap gap-x-4 gap-y-1 text-[2vmin] text-muted portrait:justify-center">
        <span>per page</span>
        {pricing.duplexEnabled && <span>· double-sided available</span>}
        <span className={lowPaper ? 'font-semibold text-warn' : ''}>
          · {config.paperCount} sheets in tray{lowPaper ? ' (running low)' : ''}
        </span>
      </div>
    </div>
  );
}

function Price({ label, value, colorful }: { label: string; value: string; colorful?: boolean }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-[2.1vmin] font-semibold tracking-wide text-muted uppercase">
        <span
          className={`inline-block size-[1.8vmin] rounded-full ${
            colorful
              ? 'bg-[conic-gradient(#f43f5e,#f59e0b,#22c55e,#3b82f6,#a855f7,#f43f5e)]'
              : 'bg-white ring-2 ring-black/60'
          }`}
        />
        {label}
      </div>
      <div className="text-[6vmin] leading-none font-black tabular-nums">{value}</div>
    </div>
  );
}
